import { markRaw } from 'vue'
import {
  FileText,
  Building2,
  GraduationCap,
  ChartColumn,
  ClipboardList,
  ClipboardCheck,
  ShieldAlert,
} from 'lucide-vue-next'
import { hasAnyPermission } from '@/lib/filters.js'

/** Modules the app knows how to route to, keyed by the names the dashboard payload uses. */
export const staticModules = [
  {
    icon: markRaw(FileText),
    to: 'policy-tracker',
    name: 'policy_tracker',
    permissions: ['view_policy']
  },
  {
    icon: markRaw(GraduationCap),
    name: 'compliance_training',
    to: 'compliance-training',
    permissions: ['view_training']
  },
  {
    icon: markRaw(Building2),
    name: 'vendor_management',
    to: 'vendor-management',
    permissions: ['view_vendor']
  },
  {
    icon: markRaw(Building2),
    name: 'employee_management',
    to: 'employee-management',
    permissions: ['view_employee']
  },
  {
    icon: markRaw(ClipboardList),
    to: 'compliance-action-center',
    name: 'compliance_action_center',
    permissions: ['view_compliance_alert']
  },
  
  {
    icon: markRaw(Building2),
    name: 'customer_management',
    to: 'onboarding-parties/customers',
    permissions: ['view_onboarding_parties']
  },
  {
    icon: markRaw(ClipboardCheck),
    name: 'audits',
    aliases: ['audit', 'auditor'],
    to: 'audits',
    permissions: ['view_audit']
  },
  {
    icon: markRaw(ChartColumn),
    to: 'reporting',
    name: 'reporting',
    permissions: ['view_reporting']
  },
  {
    icon: markRaw(ShieldAlert),
    to: 'risk-assessments',
    name: 'risk_assessment',
    aliases: ['risk_assessments', 'risk'],
    permissions: ['view_risk_assessment']
  },
  // TODO: Remove this once we have a proper policy builder
  // {
  //   icon: markRaw(TrendingUp),
  //   name: 'policy_builder',
  //   to: 'policy-builder',
  //   permissions: ['view_policy']
  // },
]

const MODULE_ORDER = [
  'policy_tracker',
  'compliance_training',
  'vendor_management',
  'employee_management',
  'customer_management',
  'audits',
  'compliance_action_center',
  'reporting',
  'risk_assessment',
  'policy_builder',
]

/** Enabled-for-workspace modules first, then the app's fixed order. */
const moduleScore = (module) => (module.workspace_is_enabled !== false ? 3 : 1)

/**
 * Whether a module (by name or alias) is switched on for the workspace whose
 * dashboard `allModules` came from. False until the dashboard has loaded — gate
 * module data fetches on this, not just on the module's permission.
 */
export function isModuleEnabledIn(allModules, moduleName) {
  const local = staticModules.find(
    (m) => m.name === moduleName || m.aliases?.includes(moduleName),
  )
  const names = local ? [local.name, ...(local.aliases ?? [])] : [moduleName]
  return (allModules ?? []).some(
    (m) => names.includes(m.name) && m.workspace_is_enabled === true,
  )
}

/** First candidate that is a finite number (numeric strings count); 0 otherwise. */
function pickNumber(...candidates) {
  for (const candidate of candidates) {
    if (candidate == null || candidate === '') continue
    const numeric = Number(candidate)
    if (Number.isFinite(numeric)) return numeric
  }
  return 0
}

/** The dashboard modules the current user may open, in display order. */
export function permittedModulesOf(allModules) {
  return [...allModules]
    .filter((module) => hasAnyPermission(module?.permissions))
    .sort((a, b) => {
      const scoreDiff = moduleScore(b) - moduleScore(a)
      if (scoreDiff !== 0) return scoreDiff
      const idxA = MODULE_ORDER.indexOf(a.name)
      const idxB = MODULE_ORDER.indexOf(b.name)
      return (idxA === -1 ? 99 : idxA) - (idxB === -1 ? 99 : idxB)
    })
}

/**
 * Turns the workspace dashboard payload into the view-model the dashboard widgets read.
 * The payload has shipped several fields under more than one name, so each accepts all of them.
 */
export function normalizeDashboard(data) {
  const {
    metrics = {},
    modules: dashboardModules = [],
    trainingAssignments,
    assignedDocuments,
    documentAssignments,
    assigned_documents,
    document_assignments,
    scoreFlags,
    policyScore,
    skipped_setup_steps: skippedSetupSteps,
    complianceScore,
    compliance_score,
    total_users,
    total_vendors,
    total_trainings,
    total_tranings,
    policies,
    policy_stats,
    policyMetrics,
    policy_metrics,
    total_policies,
  } = data ?? {}

  // Auditor / Analyst dashboard returns metrics as a card array.
  const metricsIsArray = Array.isArray(metrics)
  const metricsObj = metricsIsArray ? {} : (metrics ?? {})

  const documents =
    assignedDocuments ??
    documentAssignments ??
    assigned_documents ??
    document_assignments ??
    (!metricsIsArray ? metrics?.assignedDocuments : null) ??
    (!metricsIsArray ? metrics?.assigned_documents : null)

  const resolvedScore =
    complianceScore ??
    compliance_score ??
    policyScore ??
    metricsObj.complianceScore ??
    metricsObj.compliance_score ??
    metricsObj.policyScore ??
    metricsObj.score ??
    null

  const allModules = dashboardModules.map((dModule) => {
    const localModule = staticModules.find(
      (m) => m.name === dModule.name || m.aliases?.includes(dModule.name),
    )
    const title =
      dModule.label ||
      dModule.title ||
      String(dModule.name ?? '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
    if (!localModule) return { ...dModule, title, value: dModule.name }
    return {
      ...dModule,
      title,
      to: localModule.to,
      icon: localModule.icon,
      value: dModule.name,
      is_enabled: dModule?.is_enabled,
      workspace_is_enabled: dModule?.workspace_is_enabled,
      permissions: localModule?.permissions,
    }
  })

  return {
    stats: metricsIsArray ? {} : (metrics ?? {}),
    auditorMetrics: metricsIsArray
      ? metrics
      : Array.isArray(data?.auditor_metrics)
        ? data.auditor_metrics
        : [],
    trainings: trainingAssignments ?? [],
    assignedDocuments: Array.isArray(documents) ? documents : [],
    scoreFlags: Array.isArray(scoreFlags) ? scoreFlags : [],
    skippedSetupSteps: Array.isArray(skippedSetupSteps) ? skippedSetupSteps : [],
    complianceScore: resolvedScore,
    policyScore: resolvedScore,
    totalUsers: pickNumber(
      total_users,
      metricsObj.total_users,
      metricsObj.users,
      metricsObj.users_count,
      metricsObj.team,
    ),
    totalVendors: pickNumber(
      total_vendors,
      metricsObj.total_vendors,
      metricsObj.vendors,
      metricsObj.vendors_count,
      metricsObj.vendor_count,
    ),
    totalTrainings: pickNumber(
      total_trainings,
      total_tranings,
      metricsObj.total_trainings,
      metricsObj.total_tranings,
      metricsObj.trainings,
      metricsObj.training_courses,
      metricsObj.training_count,
    ),
    policyMetrics:
      policyMetrics ?? policy_metrics ?? metricsObj.policyMetrics ?? metricsObj.policy_metrics ?? {},
    policies:
      policies ??
      policy_stats ??
      (total_policies != null && typeof total_policies === 'object' ? total_policies : null) ??
      metricsObj.policies ??
      metricsObj.policy_stats ??
      {},
    allModules,
  }
}
